/* eslint-disable consistent-return */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { withRouter } from 'react-router-dom';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { fallbackImage, getImageFile } from 'utils/stringUtils';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  DEFAULT_TITLE,
  COMMON_META,
} from './constants';

const encodeSpecialCharacters = str =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/“/g, '&ldquo;')
    .replace(/”/g, '&rdquo;')
    .replace(/'/g, '&#039;')
    .replace(/`/g, '&#x60;')
    .replace(/’/g, '&rsquo;')
    .replace(/‘/g, '&lsquo;');

function PageMeta(props) {
  const [metadata, setMetadata] = useState({});
  const { data, type = 'homepage', ldJson, canonicalUrl, match } = props;

  useEffect(() => {
    setMetadata(getMeta());
  }, [data, match]);

  const getMeta = () => {
    const [, page, slug] = match.url.split('/');
    const topLevelPages = [
      'topics',
      'lists',
      'assessments',
      'blogs',
      'resources',
      'news',
      'best-lists',
    ];
    if (
      page !== 'company' &&
      page !== 'signup' &&
      page !== 'onboarding' &&
      type !== 'landing' &&
      slug
    ) {
      if (type === 'lists') {
        const isContentfulImage = (data.image || '').startsWith('//');

        return {
          ogTitle: data.name,
          ogDescription:
            data.description ||
            'Get started on this hand selected list of resources.',
          ogImage:
            (isContentfulImage ? `https:${data.image}` : data.image) ||
            fallbackImage,
          title: `CredibleMind | ${data.name}`,
        };
      }

      if (type === 'best-lists') {
        const { title = '', intro = '', image } = data || {};
        return {
          ogTitle: title,
          ogDescription: intro,
          ogImage: image || fallbackImage,
          title: `CredibleMind | ${title}`,
        };
      }

      if (type === 'faq') {
        const faq = _get(data, 'items.0', {});
        const availableImages = [];
        if (!_isEmpty(_get(faq, 'fields.topics'))) {
          _get(faq, 'fields.topics', []).forEach(topic => {
            if (!_isEmpty(_get(topic, 'fields.image'))) {
              availableImages.push(getImageFile(topic.fields));
            }
            if (!_isEmpty(_get(topic, 'fields.editors'))) {
              _get(topic, 'fields.editors', []).forEach(editor => {
                availableImages.push(getImageFile(editor.fields));
              });
            }
          });
        }
        const imageSource = availableImages.filter(image => image != null)[0];
        const description = documentToPlainTextString(
          _get(faq, 'fields.content', {}),
        ).substring(0, 300);

        return {
          ogTitle: _get(faq, 'fields.article'),
          ogDescription: description || DEFAULT_DESCRIPTION,
          ogImage: imageSource || DEFAULT_IMAGE,
          title: `CredibleMind | ${_get(faq, 'fields.article')}`,
        };
      }

      if (!_isEmpty(data) && !topLevelPages.includes(type)) {
        const hasEditor =
          type === 'activity' ||
          type === 'book' ||
          type === 'podcast' ||
          type === 'video';
        let editor = '';
        if (hasEditor) {
          const editorsAndAuthors = [];
          if (!_isEmpty(_get(data, 'items.0.fields.person'))) {
            _get(data, 'items.0.fields.person', []).forEach(el => {
              if (!_isEmpty(_get(el, 'fields'))) {
                editorsAndAuthors.push(el);
              }
            });
          }
          if (!_isEmpty(_get(data, 'items.0.fields.editors'))) {
            _get(data, 'items.0.fields.editors', []).forEach(el => {
              if (!_isEmpty(_get(el, 'fields'))) {
                editorsAndAuthors.push(el);
              }
            });
          }
          editor = editorsAndAuthors
            .map((el, index) => {
              if (index === 0 && page === 'blogs')
                return ` by ${_get(el, 'fields.name')}`;
              if (index === 0 && (type === 'activity' || type === 'book'))
                return ` | by ${_get(el, 'fields.name')}`;
              if (index === 0 && (type === 'podcast' || type === 'video'))
                return ` with ${_get(el, 'fields.name')}`;
              if (index === editorsAndAuthors.length - 1)
                return ` and ${_get(el, 'fields.name')}`;
              return `, ${_get(el, 'fields.name')}`;
            })
            .join('');
        }
        const imageFileData = _get(data, 'items.0.fields', {});
        let imageSource = getImageFile(imageFileData);
        if (type === 'assessment') {
          const hasSocialImage =
            _get(data, 'items.0.fields.socialImage.fields.file.url') !==
            undefined;
          if (hasSocialImage) {
            imageSource = `https:${_get(
              data,
              'items.0.fields.socialImage.fields.file.url',
            )}`;
          }
        }

        let description;
        let title;
        let pageTitle;
        if (type === 'assessment') {
          description = documentToPlainTextString(
            _get(data, 'items.0.fields.intro'),
          );
        } else if (type === 'organization') {
          description = documentToPlainTextString(
            _get(data, 'items.0.fields.intro'),
          );
        } else if (type === 'topic') {
          const hasQuickTake = !_isEmpty(
            _get(data, 'items.0.fields.quickTake'),
          );
          const hasDescription = !_isEmpty(
            _get(data, 'items.0.fields.description'),
          );
          if (hasQuickTake) {
            description = documentToPlainTextString(
              _get(data, 'items.0.fields.quickTake'),
            );
          } else if (hasDescription) {
            description = _get(data, 'items.0.fields.description');
          } else {
            description = DEFAULT_DESCRIPTION;
          }
        } else if (type === 'courses') {
          description = _get(data, 'description');
          title = _get(data, 'title');
          pageTitle = _get(data, 'title');
        } else {
          description = documentToPlainTextString(
            _get(data, 'items.0.fields.description'),
          );
        }

        title =
          title ||
          _get(data, 'items.0.fields.name') ||
          _get(data, 'items.0.fields.title') ||
          _get(data, 'items.0.fields.article');
        pageTitle = pageTitle || `CredibleMind | ${title}`;
        return {
          ogTitle: `${title}${editor}`,
          ogDescription: description
            ? description.replace(/["]+/g, '').substring(0, 300)
            : undefined,
          ogImage: imageSource,
          title: pageTitle,
        };
      }
    } else if (
      !_isEmpty(data) &&
      (topLevelPages.includes(type) || page === 'company')
    ) {
      const { title, description, image } = data;
      return {
        ogTitle: title,
        ogDescription: description,
        ogImage: image,
        title,
      };
    } else if (!_isEmpty(data) && type === 'landing') {
      const hasClient = !_isEmpty(_get(data, 'client'));
      const finalData = hasClient ? _get(data, 'client') : data;
      if (hasClient) {
        const clientMetadata = _get(finalData, 'metadata', {});
        const shortName = _get(finalData, 'shortName', '');
        const image = _get(finalData, 'logoReverse.url');
        const title = _get(clientMetadata, 'title');
        const description = _get(clientMetadata, 'description');
        return {
          ogTitle: title,
          ogDescription: description,
          ogImage: image,
          title,
          ...(shortName
            ? { url: `https://${shortName}.crediblemind.com/` }
            : {}),
        };
      }
      const title = _get(finalData, 'title');
      const description = _get(finalData, 'description');
      const image = _get(finalData, 'image');
      return {
        ogTitle: title,
        ogDescription: description,
        ogImage: image,
        title,
      };
    } else {
      const { title } = COMMON_META[type];
      return {
        ogTitle: `${title} - CredibleMind`,
        ogDescription: DEFAULT_DESCRIPTION,
        ogImage: DEFAULT_IMAGE,
        title: `CredibleMind | ${title}`,
      };
    }
  };

  const {
    ogTitle = DEFAULT_TITLE,
    ogDescription = DEFAULT_DESCRIPTION,
    ogImage = DEFAULT_IMAGE,
    title = DEFAULT_TITLE,
    url = window.location.href,
  } = metadata;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={ogTitle} />
      <meta
        name="description"
        content={encodeSpecialCharacters(ogDescription)}
      />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={ogTitle} />
      <meta
        property="og:description"
        content={encodeSpecialCharacters(ogDescription)}
      />
      <meta property="og:image" content={ogImage} />
      <meta
        property="og:image:alt"
        content={encodeSpecialCharacters(ogDescription)}
      />
      {ldJson && (
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      )}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    </Helmet>
  );
}

PageMeta.propTypes = {
  ldJson: PropTypes.object,
  canonicalUrl: PropTypes.string,
  data: PropTypes.any,
  type: PropTypes.string,
  match: PropTypes.object,
};

export default withRouter(PageMeta);
